import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import EmptyState from '~components/EmptyState';
import StatusBar from '~pages/Dialler/AsteriskDiallerV2/AsteriskStatusBar';
import ControlCard, { AgentChannelDisplay, CustomerChannelDisplay } from '~pages/Dialler/AsteriskDiallerV2/ControlCard';
import DetailCard from '~pages/Dialler/AsteriskDiallerV2/DetailCard';
import ManualOutboundPanel from '~pages/Dialler/AsteriskDiallerV2/ManualOutboundPanel';
import OfferedCallPanel from '~pages/Dialler/AsteriskDiallerV2/OfferedCallPanel';
import { useAssignedDiallerGroup } from '~providers/AssignedDiallerGroupProvider';
import { useAsteriskDialler } from '~providers/AsteriskDiallerProvider';
import { AgentControl, AgentState, ChannelAction, CustomerChannel } from '~services/AsteriskDiallerManager/domain';

const toCustomerChannels = (channels: { [key: string]: CustomerChannel }): CustomerChannelDisplay[] =>
  Object.values(channels).map((ch) => {
    const lastState = ch.stateHistory?.at(-1);
    return {
      key: ch.key,
      address: ch.address,
      state: lastState?.state ?? '',
      since: lastState?.changeTime ?? '',
      isMuted: ch.muted,
    };
  });

const toAgentChannels = (
  agentControls: { [key: string]: AgentControl },
  pendingAgentControls: { [key: string]: AgentControl },
): AgentChannelDisplay[] => {
  const confirmed = Object.entries(agentControls)
    .filter(([, ac]) => ac.channel !== null)
    .map(([key, ac]) => {
      const lastState = ac.channel!.stateHistory?.at(-1);
      return { key, username: ac.username, state: lastState?.state ?? '', since: lastState?.changeTime ?? '' };
    });

  // pendingAgentControls are conference participants still ringing (App.svelte:1643-1646)
  const pending = Object.entries(pendingAgentControls)
    .filter(([, ac]) => ac.channel !== null)
    .map(([key, ac]) => {
      const lastState = ac.channel!.stateHistory?.at(-1);
      return { key, username: ac.username, state: 'Ringing', since: lastState?.changeTime ?? '' };
    });

  return confirmed.concat(pending);
};

// Conference is blocked when any customer channel is Connected, Ringing, or Initialised (App.svelte:1649-1659)
const CONFERENCE_BLOCKING_STATES = ['Connected', 'Ringing', 'Initialised'];

const getCanConference = (
  isConnected: boolean,
  isAfterCallWork: boolean,
  customerChannels: CustomerChannel[],
): boolean => {
  if (!isConnected || isAfterCallWork) return false;
  return !customerChannels.some((ch) => {
    const state = ch.stateHistory?.at(-1)?.state ?? '';
    return CONFERENCE_BLOCKING_STATES.includes(state);
  });
};

const getCanLeave = (
  isConnected: boolean,
  isPreview: boolean,
  onHold: boolean,
  allowAgentLeaveCall: boolean,
  customerChannelCount: number,
  agentChannelCount: number,
): boolean =>
  isPreview || (isConnected && !onHold && (allowAgentLeaveCall || customerChannelCount > 1 || agentChannelCount > 1));

export const AsteriskDiallerV2 = () => {
  const {
    state: dialler,
    dispatch,
    socketRef,
    manualOutboundEnabled,
    allowAgentLeaveCall,
    canPauseCallRecordings,
  } = useAsteriskDialler();
  const assignedGroup = useAssignedDiallerGroup();
  const [conferenceRequested, setConferenceRequested] = useState(false);
  useEffect(() => {
    setConferenceRequested(false);
  }, [dialler.call]);

  // Group switch guard — block switchGroup() during active call states (AsteriskDialler/index.tsx:110-161)
  useEffect(() => {
    const unsafeStates: string[] = [AgentState.Connected, AgentState.AfterCallWork, AgentState.InQueue];
    const safeToSwitch = !unsafeStates.includes(dialler.state);
    console.log(
      `safeToSwitch: ${safeToSwitch} dialler state: ${dialler.state} assigned group exists: ${
        assignedGroup.configuredGroup !== undefined
      }`,
    );
    if (safeToSwitch && assignedGroup.configuredGroup !== undefined) {
      assignedGroup.switchGroup();
    }
  }, [dialler.state, assignedGroup.configuredGroup]);

  // Emits changes around group change
  useEffect(() => {
    socketRef.current?.changeGroup(
      assignedGroup.configuredGroup?.diallerGroupId || assignedGroup.group.diallerGroupId,
      assignedGroup.configuredGroup?.diallerType || assignedGroup.group.diallerType,
      assignedGroup.configuredGroup?.campaignType || assignedGroup.group.campaignType,
    );
  }, [
    assignedGroup.configuredGroup?.diallerGroupId,
    assignedGroup.configuredGroup?.diallerType,
    assignedGroup.configuredGroup?.campaignType,
  ]);

  const hasActiveCall =
    dialler.state === AgentState.Connected ||
    dialler.state === AgentState.AfterCallWork ||
    (dialler.state === AgentState.InQueue && dialler.call !== null) ||
    (dialler.state === AgentState.Preview && dialler.call !== null);
  const isConnected = dialler.state === AgentState.Connected;
  const isPreview = dialler.state === AgentState.Preview;

  // Derived capabilities (mirrors callInformation handler in App.svelte)
  const customerChannels = dialler.call ? Object.values(dialler.call.customerChannels) : [];
  const agentChannelCount = dialler.call
    ? Object.keys(dialler.call.agentControls).length + Object.keys(dialler.call.pendingAgentControls).length
    : 0;
  const onHold = customerChannels.some((ch) => ch.stateHistory?.at(-1)?.state === 'On Hold');

  const canLeave = getCanLeave(
    isConnected,
    isPreview,
    onHold,
    allowAgentLeaveCall,
    customerChannels.length,
    agentChannelCount,
  );

  const isAfterCallWork = dialler.state === AgentState.AfterCallWork;
  const canConference = getCanConference(isConnected, isAfterCallWork, customerChannels) && !conferenceRequested;
  const canMute = isConnected && !isAfterCallWork;

  const onStatusChange = (status: string) => {
    if (!socketRef.current) {
      console.error('socket not found');
      return;
    }

    socketRef.current.setState(status);
  };

  if (!dialler.connected) {
    return <EmptyState type='puzzle' text='Not Connected' subText='Unable to connect to server. Retrying...' />;
  }

  return (
    <>
      <StatusBar
        statusList={dialler.campaignInformation.agentStatuses}
        status={dialler.state}
        statusTimestamp={dialler.stateTime}
        pendingStatus={dialler.pendingState}
        campaign={dialler.campaignInformation.name}
        setAgentState={onStatusChange}
        showViewCall={
          dialler.pendingPreviewCall !== null && !dialler.canCallPreview && (dialler.call === null || dialler.canDispo)
        }
        showCallPreview={dialler.state === AgentState.Preview && dialler.canCallPreview}
        onViewCall={() => socketRef.current?.setState('Preview')}
        onCallPreview={() => dialler.call && socketRef.current?.previewCall(dialler.call.id)}
        manualOutboundOpen={dialler.manualOutboundOpen}
      />
      <Grid container spacing={2}>
        {/* Status is Unavailable && No pending preview call && No offered call => Offline message + Manual Outbound button (Offline Page) */}
        {dialler.state === AgentState.Unavailable &&
          dialler.pendingPreviewCall === null &&
          dialler.offeredCall === null && (
            <>
              <Grid size={12}>
                <EmptyState type='offline' text='You are currently offline.' subText='Go online and accept calls!' />
              </Grid>
              {manualOutboundEnabled && !dialler.manualOutboundOpen && (
                <Grid
                  size={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: -3,
                  }}>
                  <Button
                    variant='contained'
                    color='primary'
                    disableElevation
                    onClick={() => dispatch({ type: 'SET_MANUAL_OUTBOUND_OPEN', payload: true })}
                    sx={{ textTransform: 'none' }}>
                    Prepare Outbound Call
                  </Button>
                </Grid>
              )}
              {manualOutboundEnabled && dialler.manualOutboundOpen && (
                <Grid
                  size={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 600,
                    }}>
                    <ManualOutboundPanel />
                  </Box>
                </Grid>
              )}
            </>
          )}

        {/* Pending preview call exists => Blue banner showing preview lead name + skills (Preview Banner) */}
        {dialler.pendingPreviewCall !== null && (
          <Grid size={12}>
            <Box sx={{ backgroundColor: 'primary.main', borderRadius: 1, py: 1, px: 2, mt: 1 }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  color: '#fff',
                  textAlign: 'center',
                }}>
                Pending preview call: {dialler.pendingPreviewCall.lead.name}
                {dialler.pendingPreviewCall.lead.requiredSkills !== '' &&
                  ` - ${dialler.pendingPreviewCall.lead.requiredSkills}`}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Status is Connected || AfterCallWork || (InQueue with call) => ControlCard + DetailCard (Active Call Page) */}
        {hasActiveCall && (
          <>
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}>
              <ControlCard
                username={dialler.username}
                state={dialler.state}
                agentMuted={dialler.agentMuted}
                isRecording={dialler.call?.isRecording ?? false}
                canPauseCallRecordings={canPauseCallRecordings}
                canLeave={canLeave}
                canConference={canConference}
                canMute={canMute}
                customerChannels={dialler.call ? toCustomerChannels(dialler.call.customerChannels) : []}
                agentChannels={
                  dialler.call ? toAgentChannels(dialler.call.agentControls, dialler.call.pendingAgentControls) : []
                }
                agentStates={dialler.agentStates}
                onSendDtmf={(digit) => socketRef.current?.sendDTMF(digit)}
                onLeaveCall={() => dialler.call && socketRef.current?.leaveCall(dialler.call.id)}
                onSetAgentMuted={(muted) => socketRef.current?.setMute(muted)}
                onSetRecording={(recording) => socketRef.current?.setRecording(recording)}
                onInternalConference={(username) => {
                  setConferenceRequested(true);
                  socketRef.current?.initiateInternalConference(username);
                }}
                onExternalConference={(endpoint) => {
                  setConferenceRequested(true);
                  socketRef.current?.initiateExternalConference(endpoint);
                }}
                onChannelAction={(key, action) =>
                  socketRef.current?.customerChannelAction(key, action as ChannelAction)
                }
                onRemoveAgentFromConference={(u) => socketRef.current?.removeInternalConferenceParticipant(u)}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}>
              {/* AfterCallWork && offered call exists => OfferedCallPanel ABOVE DetailCard */}
              {dialler.offeredCall !== null && dialler.state === AgentState.AfterCallWork && <OfferedCallPanel />}
              <DetailCard
                name={dialler.call?.lead.name ?? ''}
                callType={dialler.callType || 'Outbound'}
                leadId={dialler.call ? String(dialler.call.lead.leadId) : ''}
                externalId={dialler.call?.lead.externalId ?? ''}
                timezone={dialler.call?.lead.timezone ?? ''}
                endpoint={dialler.call?.lead.endpoints[0] ?? ''}
                requiredSkills={dialler.call?.lead.requiredSkills ?? ''}
                campaign={dialler.call?.lead.campaignName ?? dialler.campaignInformation.name}
                attributes={dialler.call?.lead.attributes ?? {}}
                endpointAttributes={
                  dialler.call?.lead.endpointAttributes[dialler.call?.lead.endpoints[0]]?.attributes ?? {}
                }
                callbackNotes={dialler.call?.lead.callbackNotes ?? null}
              />
            </Grid>
          </>
        )}
        {/* No active call && Another agent is offering a call transfer => Lead info + Accept/Reject buttons standalone (Offered Call Page) */}
        {!hasActiveCall && dialler.offeredCall !== null && (
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 600,
              }}>
              <OfferedCallPanel />
            </Box>
          </Grid>
        )}
        {/* Status is InQueue && No active call && No offered call => "Waiting for calls..." message (Queue Waiting Page) */}
        {dialler.state === AgentState.InQueue && !hasActiveCall && dialler.offeredCall === null && (
          <Grid size={12}>
            <EmptyState
              type='call-waiting'
              text={`Hello ${dialler.username},`}
              subText='You are in queue and ready to receive calls.'
            />
          </Grid>
        )}
        {/* No active call && Not Unavailable && Not InQueue && No offered call (e.g. Preview, Wrap) => "You are in the state X" message (Other State Page) */}
        {!hasActiveCall &&
          dialler.state !== AgentState.Unavailable &&
          dialler.state !== AgentState.InQueue &&
          dialler.offeredCall === null && (
            <Grid size={12}>
              <EmptyState
                type='offline'
                text={`You are in the state ${dialler.state}`}
                subText='Go online and accept calls!'
              />
            </Grid>
          )}
      </Grid>
    </>
  );
};

export default AsteriskDiallerV2;
